import type {
  AiCommentEntityType,
  AiCommentItem,
} from "@/app/api/types/aiComment";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import { useAiAssistantName } from "@/query/hooks/useAiAssistantName";
import {
  formatZoned,
  useCompanyTimezone,
} from "@/lib/datetime/companyTimezone";
import { cn } from "@/lib/utils";
import { openAssistantReplyingToNote } from "@/components/ai/replyToNote";
import { AiCommentBody } from "@/components/ai/AiCommentBody";
import { useAiCommentMutations } from "@stockline/web";
import {
  AlertTriangle,
  CornerUpLeft,
  Loader2,
  MessageSquareText,
  Trash2,
  X,
} from "lucide-react";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export { AiCommentBody } from "@/components/ai/AiCommentBody";

interface AiCommentIndicatorProps {
  comments: AiCommentItem[];
  entityType: AiCommentEntityType;
  entityId: string;
  size?: "sm" | "md";
  className?: string;
  /** "icon" (default) = compact square icon button used in lists/cells.
   *  "labeled" = prominent violet button with an "AI thoughts" label + count,
   *  used on the focus-page Client section. */
  variant?: "icon" | "labeled";
  /** Optional stable test hook for the trigger button (e.g. the focus-page
   *  "AI thoughts" button). Applied to whichever trigger is mounted (closed
   *  shell or open PopoverTrigger). */
  testId?: string;
}

// Comment timestamps arrive in two shapes:
//   V2 TS path (current): `new Date().toISOString()` → "2026-05-15T12:34:56.789Z"
//   Legacy Python path:   naive UTC                 → "2026-05-15T12:34:56.789"
// Appending "Z" unconditionally turned the V2 shape into "...ZZ" → Invalid Date,
// which fell through every numeric comparison and surfaced as "Invalid Date".
export function formatAiCommentRelativeTime(
  dateStr: string,
  tz: string,
  translate: (key: string, count?: number) => string,
): string {
  if (!dateStr) return "";
  const hasTz = dateStr.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(dateStr);
  const date = new Date(hasTz ? dateStr : dateStr + "Z");
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return translate("common.aiComments.justNow");
  if (diffMin < 60) return translate("common.aiComments.minutesAgo", diffMin);

  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return translate("common.aiComments.hoursAgo", diffHrs);

  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return translate("common.aiComments.daysAgo", diffDays);

  return formatZoned(dateStr, tz, "P");
}

/**
 * Lazy-mount shell. When closed, renders only a static <button> + count
 * badge — no Radix Popover machinery. The full popover subtree (Radix
 * Popover + useCompanyTimezone) only mounts after the user actually clicks.
 * Matches the lazy pattern in PreOrderInfoPopover.
 *
 * Critical on the recap list: every row with AI comments would otherwise
 * pay the full Popover Root + Trigger tax up front, none of which is
 * exercised until the user opens the popover.
 */
export function AiCommentIndicator({
  comments,
  entityType,
  entityId,
  size = "sm",
  className,
  variant = "icon",
  testId,
}: AiCommentIndicatorProps) {
  if (!comments || comments.length === 0) return null;
  return (
    <AiCommentIndicatorMounted
      comments={comments}
      entityType={entityType}
      entityId={entityId}
      size={size}
      className={className}
      variant={variant}
      testId={testId}
    />
  );
}

function AiCommentIndicatorMounted({
  comments,
  entityType,
  entityId,
  size,
  className,
  variant = "icon",
  testId,
}: Readonly<AiCommentIndicatorProps>) {
  const { t } = useTranslation();
  const aiName = useAiAssistantName();
  const { removeComment } = useAiCommentMutations();
  const [open, setOpen] = useState(false);
  const [removedCommentIds, setRemovedCommentIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null,
  );
  const visibleComments = comments.filter(
    (comment) => !removedCommentIds.has(comment.id),
  );
  // The open/close transition swaps DOM nodes (closed shell <button> ↔
  // open PopoverTrigger <button>), so keyboard focus is lost on each
  // toggle. Restore it on the new trigger when we're back to the closed
  // shell after having been opened — covers Escape, click-outside, and
  // explicit close. `hadOpenedRef` skips the very first render (no
  // previous trigger to focus on).
  const closedButtonRef = useRef<HTMLButtonElement>(null);
  const hadOpenedRef = useRef(false);
  useEffect(() => {
    if (open) {
      hadOpenedRef.current = true;
      return;
    }
    if (hadOpenedRef.current) {
      closedButtonRef.current?.focus();
    }
  }, [open]);

  if (visibleComments.length === 0) return null;

  const hasFlagged = visibleComments.some((c) => c.flag);
  const isLabeled = variant === "labeled";
  const iconSize = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  const buttonSize = size === "sm" ? "h-6 w-6" : "h-7 w-7";
  // Flagged comments read a notch stronger (deeper violet fill). Extracted out
  // of the cn() calls so the variant switch below isn't a nested ternary.
  const labeledStateClass = hasFlagged
    ? "border-violet-300 bg-violet-100 text-violet-800 hover:bg-violet-200"
    : "border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 hover:border-violet-400";
  const iconStateClass = hasFlagged
    ? "bg-violet-100 text-violet-700 hover:bg-violet-200 dark:bg-violet-900/40 dark:text-violet-300 dark:hover:bg-violet-900/60"
    : "bg-violet-50 text-violet-500 hover:bg-violet-100 hover:text-violet-700 dark:bg-violet-900/20 dark:text-violet-300 dark:hover:bg-violet-900/40";
  const buttonClass = isLabeled
    ? cn(
        "inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-sm font-semibold transition-colors",
        labeledStateClass,
        className,
      )
    : cn(
        "relative inline-flex items-center justify-center rounded-md border border-transparent transition-colors",
        buttonSize,
        iconStateClass,
        className,
      );
  const label = isLabeled ? t("common.aiComments.noteButton") : undefined;
  const title = t("common.aiComments.tooltip", {
    count: visibleComments.length,
    name: aiName,
  });
  const showBadge = hasFlagged || visibleComments.length > 1;
  const badge = showBadge ? (
    <span
      className={cn(
        "absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-0.5 text-[9px] font-medium",
        hasFlagged
          ? "bg-violet-500 text-white"
          : "border border-violet-400 text-violet-600 bg-white dark:bg-gray-900 dark:border-violet-500 dark:text-violet-400",
      )}
    >
      {hasFlagged ? "!" : visibleComments.length}
    </span>
  ) : null;

  const commentRemoved = (commentId: string) => {
    setRemovedCommentIds((current) => {
      const next = new Set(current);
      next.add(commentId);
      return next;
    });
  };

  const discardComment = async (commentId: string) => {
    setDeletingCommentId(commentId);
    try {
      await removeComment(entityType, entityId, commentId);
      commentRemoved(commentId);
      toast.success(t("common.aiComments.discarded"));
    } catch {
      toast.error(t("common.aiComments.discardError"));
    } finally {
      setDeletingCommentId(null);
    }
  };

  const indicator = !open ? (
    <TriggerButton
      ref={closedButtonRef}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(true);
      }}
      className={buttonClass}
      title={title}
      iconSize={iconSize}
      badge={badge}
      label={label}
      count={visibleComments.length}
      testId={testId}
    />
  ) : (
    <AiCommentIndicatorOpen
      comments={visibleComments}
      entityType={entityType}
      entityId={entityId}
      aiName={aiName}
      iconSize={iconSize}
      buttonClass={buttonClass}
      title={title}
      badge={badge}
      label={label}
      count={visibleComments.length}
      testId={testId}
      onClose={() => setOpen(false)}
      deletingCommentId={deletingCommentId}
      onDiscard={(commentId) => void discardComment(commentId)}
    />
  );

  return indicator;
}

interface TriggerButtonProps {
  onClick: (e: React.MouseEvent) => void;
  className: string;
  title: string;
  iconSize: string;
  badge: React.ReactNode;
  /** When set, renders the text after the icon (labeled variant) and the
   *  count inline instead of the corner badge. */
  label?: string;
  count?: number;
  /** Optional stable test hook applied to the rendered <button>. */
  testId?: string;
}

const TriggerButton = forwardRef<HTMLButtonElement, TriggerButtonProps>(
  function TriggerButton(
    { onClick, className, title, iconSize, badge, label, count, testId },
    ref,
  ) {
    return (
      <button
        type="button"
        ref={ref}
        onClick={onClick}
        className={className}
        title={title}
        data-testid={testId}
      >
        <MessageSquareText className={iconSize} />
        {label ? (
          <>
            <span>{label}</span>
            {typeof count === "number" && (
              <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-200 px-1 text-[10px] font-medium text-violet-800">
                {count}
              </span>
            )}
          </>
        ) : (
          badge
        )}
      </button>
    );
  },
);

interface AiCommentIndicatorOpenProps {
  comments: AiCommentItem[];
  entityType: AiCommentEntityType;
  entityId: string;
  aiName: string;
  iconSize: string;
  buttonClass: string;
  title: string;
  badge: React.ReactNode;
  label?: string;
  count?: number;
  testId?: string;
  onClose: () => void;
  deletingCommentId: string | null;
  onDiscard: (commentId: string) => void;
}

function AiCommentIndicatorOpen({
  comments,
  entityType,
  entityId,
  aiName,
  iconSize,
  buttonClass,
  title,
  badge,
  label,
  count,
  testId,
  onClose,
  deletingCommentId,
  onDiscard,
}: Readonly<AiCommentIndicatorOpenProps>) {
  const { t } = useTranslation();
  const tz = useCompanyTimezone();

  return (
    <Popover
      open={true}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <PopoverTrigger asChild>
        <TriggerButton
          onClick={(e) => e.stopPropagation()}
          className={buttonClass}
          title={title}
          iconSize={iconSize}
          badge={badge}
          label={label}
          count={count}
          testId={testId}
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(24rem,calc(100vw-2rem))] p-0"
        align="start"
        side="bottom"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-violet-100 px-3 py-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-violet-600">
              {t("common.aiComments.title", { name: aiName })}
            </span>
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-100 px-1 text-[10px] font-medium text-violet-600">
              {comments.length}
            </span>
          </div>
          <button
            type="button"
            aria-label={t("common.close")}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Comments list */}
        <div className="max-h-72 overflow-y-auto">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className={cn(
                "flex items-start gap-2 border-b border-gray-50 px-3 py-2.5 last:border-0",
                comment.flag &&
                  "bg-violet-50 border-l-2 border-l-violet-500 border-b-violet-100 dark:bg-violet-950/30 dark:border-l-violet-400",
              )}
            >
              {comment.flag && (
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-400" />
              )}
              <div className="flex-1 min-w-0">
                {comment.comment && (
                  <AiCommentBody
                    text={comment.comment}
                    showMoreLabel={t("common.aiComments.showMore")}
                    showLessLabel={t("common.aiComments.showLess")}
                    flag={comment.flag}
                  />
                )}
                {!comment.comment && comment.flag && (
                  <span className="text-xs font-medium text-violet-700 dark:text-violet-300">
                    {t("common.aiComments.flaggedForReview")}
                  </span>
                )}
                <div className="mt-2 flex items-center justify-between gap-2 border-t border-violet-100/80 pt-2 text-[10px] text-muted-foreground dark:border-violet-900/60">
                  <span>
                    {formatAiCommentRelativeTime(
                      comment.created_at,
                      tz,
                      (key, count) => t(key, { count }),
                    )}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className="inline-flex h-7 items-center gap-1.5 rounded-md bg-violet-600 px-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 dark:bg-violet-500 dark:hover:bg-violet-400"
                      onClick={() => {
                        openAssistantReplyingToNote({
                          id: comment.id,
                          comment: comment.comment ?? null,
                          agentExecutionId: comment.agent_execution_id ?? null,
                          entityType,
                          entityId,
                        });
                        onClose();
                      }}
                    >
                      <CornerUpLeft className="h-3.5 w-3.5" />
                      {t("common.aiComments.reply")}
                    </button>
                    <button
                      type="button"
                      aria-label={t("common.aiComments.discard")}
                      title={t("common.aiComments.discard")}
                      disabled={deletingCommentId !== null}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:hover:bg-gray-800 dark:hover:text-red-400"
                      onClick={() => onDiscard(comment.id)}
                    >
                      {deletingCommentId === comment.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
